/**
 * Éditeur V2 ouvert depuis un signalement ou sur une question précise
 * (`?question=<bloc>&report=<id>`, migration 0348) :
 *  - bandeau du signalement (qui, quoi, statut) avec « Traité » / « Ignorer » ;
 *  - « Réviser avec l'IA » : recommandations (préremplies avec le signalement)
 *    → aperçu de la question corrigée → « Appliquer » (exercise-blocks-mutate) ;
 *  - défilement jusqu'à la question, mise en évidence quelques secondes.
 */

import { useEffect, useState } from 'react';
import { Flag, Lightbulb, Sparkles } from 'lucide-react';
import { Button, cn, MarkdownMath } from '@jeraisonne/ui';
import { useBlocksMutate } from '@/lib/exercise-v2/api';
import {
  QUESTION_REPORT_STATUS_LABELS,
  reportInstructions,
  useQuestionReport,
  useReviseQuestion,
  useSetReportStatus,
  type QuestionRevisionPreview,
} from '@/lib/exercise-v2/question-reports';
import type { BlockMutationOp } from '@/lib/exercise-v2/types';

/** Fait défiler jusqu'au bloc et le met en évidence (l'éditeur charge après coup). */
function useScrollToBlock(blockId: string | undefined) {
  useEffect(() => {
    if (!blockId) return;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const find = () => {
      const el = document.querySelector<HTMLElement>(`[data-testid="block-${blockId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-4', 'ring-amber-300', 'rounded-xl');
        timer = setTimeout(() => el.classList.remove('ring-4', 'ring-amber-300'), 4000);
        return;
      }
      if (tries++ < 40) timer = setTimeout(find, 150);
    };
    find();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [blockId]);
}

export function QuestionReportPanel({
  exerciseId,
  questionBlockId,
  reportId,
}: {
  exerciseId: string;
  questionBlockId?: string;
  reportId?: string;
}) {
  const report = useQuestionReport(reportId);
  const setStatus = useSetReportStatus();
  const revise = useReviseQuestion();
  const mutate = useBlocksMutate(exerciseId);
  const questionId = questionBlockId ?? report.data?.question_block_id ?? undefined;
  useScrollToBlock(questionId);

  const [instructions, setInstructions] = useState('');
  const [prefilled, setPrefilled] = useState(false);
  const [proposal, setProposal] = useState<{
    ops: BlockMutationOp[];
    preview: QuestionRevisionPreview;
  } | null>(null);
  const [resolveAfter, setResolveAfter] = useState(true);
  const [applied, setApplied] = useState(false);

  // Recommandations préremplies avec le texte du signalement (une seule fois).
  useEffect(() => {
    if (!prefilled && report.data) {
      setInstructions(reportInstructions(report.data));
      setPrefilled(true);
    }
  }, [prefilled, report.data]);

  if (!questionId && !reportId) return null;
  const r = report.data;

  async function propose() {
    if (!questionId) return;
    setApplied(false);
    setProposal(null);
    try {
      setProposal(
        await revise.mutateAsync({ exerciseId, questionBlockId: questionId, instructions }),
      );
    } catch {
      /* affiché via revise.error */
    }
  }

  async function apply() {
    if (!proposal) return;
    if (proposal.ops.length > 0) await mutate.mutateAsync(proposal.ops);
    if (r && resolveAfter && r.status === 'open') {
      await setStatus.mutateAsync({ id: r.id, status: 'resolved' });
    }
    setApplied(true);
    setProposal(null);
  }

  return (
    <section
      aria-label="Signalement et révision"
      className="mx-auto mb-4 mt-3 max-w-3xl space-y-3 px-3 sm:px-0"
    >
      {r && (
        <div className="space-y-2 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-2">
            {r.kind === 'improvement' ? (
              <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
            ) : (
              <Flag className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {r.kind === 'improvement' ? 'Amélioration proposée' : 'Question signalée'}
                {r.reporter_name ? ` par ${r.reporter_name.split(' ')[0]}` : ''}
              </p>
              <p className="mt-1 whitespace-pre-line text-sm text-amber-900">« {r.message} »</p>
            </div>
            <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-amber-800">
              {QUESTION_REPORT_STATUS_LABELS[r.status]}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {r.status === 'open' ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ id: r.id, status: 'resolved' })}
                >
                  Marquer comme traité
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ id: r.id, status: 'dismissed' })}
                >
                  Ignorer
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate({ id: r.id, status: 'open' })}
              >
                Rouvrir
              </Button>
            )}
          </div>
        </div>
      )}

      {questionId && (
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            <Sparkles className="text-primary-500 h-4 w-4" aria-hidden /> Réviser cette question
            avec l’IA
          </p>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={4}
            maxLength={4000}
            placeholder="Tes recommandations : ce qui est faux, ce qu’il faut préciser, simplifier…"
            aria-label="Recommandations pour l’IA"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
          <Button
            fullWidth
            onClick={propose}
            disabled={revise.isPending || instructions.trim().length < 3}
          >
            {revise.isPending ? 'L’IA révise la question…' : 'Proposer une version corrigée'}
          </Button>
          {revise.isError && (
            <p role="alert" className="text-sm text-red-600">
              {revise.error instanceof Error ? revise.error.message : 'Erreur inconnue.'}
            </p>
          )}
          {applied && (
            <p role="status" className="text-sm font-semibold text-green-700">
              Question mise à jour.
            </p>
          )}

          {proposal && (
            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
              {proposal.preview.summary && (
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-800">Changements : </span>
                  {proposal.preview.summary}
                </p>
              )}
              <div className="text-sm text-slate-900">
                <MarkdownMath>{proposal.preview.prompt_md}</MarkdownMath>
              </div>
              {proposal.preview.steps.map((s, i) => (
                <div key={i} className="space-y-1 rounded-lg bg-white p-2 text-sm">
                  {proposal.preview.steps.length > 1 && (
                    <p className="text-xs font-semibold uppercase text-slate-400">Étape {i + 1}</p>
                  )}
                  <div className="flex gap-2 text-green-700">
                    <span aria-hidden>✓</span>
                    <MarkdownMath>{s.answer_md}</MarkdownMath>
                  </div>
                  {s.distractors.map((d, j) => (
                    <div
                      key={j}
                      className={cn(
                        'flex gap-2',
                        d.is_correct ? 'text-green-700' : 'text-slate-500',
                      )}
                    >
                      <span aria-hidden>{d.is_correct ? '✓' : '✗'}</span>
                      <MarkdownMath>{d.label_md}</MarkdownMath>
                    </div>
                  ))}
                  <div className="border-t border-slate-100 pt-1 text-slate-600">
                    <MarkdownMath>{s.explanation_md}</MarkdownMath>
                  </div>
                </div>
              ))}
              {r && r.status === 'open' && (
                <label className="flex min-h-[2.75rem] items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={resolveAfter}
                    onChange={(e) => setResolveAfter(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Marquer le signalement comme traité
                </label>
              )}
              {proposal.ops.length === 0 && (
                <p className="text-sm text-slate-500">L’IA n’a rien changé à la question.</p>
              )}
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button variant="outline" fullWidth onClick={() => setProposal(null)}>
                  Annuler
                </Button>
                <Button fullWidth onClick={apply} disabled={mutate.isPending}>
                  {mutate.isPending ? 'Application…' : 'Appliquer'}
                </Button>
              </div>
              {mutate.isError && (
                <p role="alert" className="text-sm text-red-600">
                  {mutate.error instanceof Error ? mutate.error.message : 'Erreur inconnue.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
